import { useState, useRef, useEffect, useCallback } from 'react';
import { useAvatarRAF } from './useAvatarRAF';

/**
 * Physics configuration constants
 */
const MOVEMENT_CONFIG = {
  // Movement physics
  SPEED: 0.2, // Consistent slow speed for all avatars
  COLLISION_MARGIN: 112, // Tighter float range (matches original)
  RETURN_POSITION_SPEED_MULT: 3, // Return-to-origin is faster than idle float
  BOUNCE_RANDOMNESS: 6, // ±3 degrees randomness on wall bounce
  CONTAINER_SIZE: 240, // Avatar container dimensions
  CENTER_POSITION: 120, // Center point of container

  // 3D rotation limits and speeds
  ROTATION: {
    MAX_DEGREES: 2, // Maximum ±2 degrees rotation for natural sway
    SPEED_MIN: 0.005, // Minimum rotation speed
    SPEED_MAX: 0.015, // Maximum rotation speed
    RETURN_SPEED: 0.1, // Speed of rotation return to center
  },

  // Animation timing
  MOVEMENT_FRAME_SKIP: 2, // Update position every 2nd frame (30fps movement, 60fps rendering)
} as const;

/**
 * 3D rotation state
 */
interface Rotation3D {
  x: number;
  y: number;
  z: number;
}

/**
 * 2D position state
 */
interface Position2D {
  x: number;
  y: number;
}

/**
 * Internal velocity state
 */
interface Velocity {
  angle: number; // Movement direction in degrees
  speed: number; // Movement speed
}

/**
 * Props for useAvatarMovement hook
 */
interface UseAvatarMovementProps {
  /** Whether floating/movement is enabled */
  enableFloat?: boolean;
  /** Avatar container size (default: 240) */
  containerSize?: number;
}

/**
 * Return type for useAvatarMovement hook
 */
interface UseAvatarMovementReturn {
  /** Current 2D position for avatar positioning */
  position: Position2D;
  /** Current 3D rotation for transform styles */
  rotation: Rotation3D;
  /** Whether avatar is currently returning to center */
  isReturning: boolean;
}

/**
 * Hook for avatar spatial movement and physics
 *
 * Provides realistic balloon-like movement with bouncing physics and natural
 * 3D rotation. Uses the central RAF scheduler for optimal performance.
 *
 * @param props - Configuration for movement behavior
 * @returns Current position, rotation, and return state
 */
export function useAvatarMovement({
  enableFloat = true,
  containerSize = MOVEMENT_CONFIG.CONTAINER_SIZE,
}: UseAvatarMovementProps = {}): UseAvatarMovementReturn {
  const { subscribe } = useAvatarRAF();

  // Calculate center and margins based on container size
  const centerPosition = containerSize / 2;
  const collisionMargin = MOVEMENT_CONFIG.COLLISION_MARGIN;

  // Position state - start at center
  const [position, setPosition] = useState<Position2D>({
    x: centerPosition,
    y: centerPosition,
  });

  // 3D rotation state
  const [rotation, setRotation] = useState<Rotation3D>({ x: 0, y: 0, z: 0 });

  // Return-to-center state
  const [isReturning, setIsReturning] = useState(false);

  // Internal physics state (refs to avoid re-renders)
  const velocityRef = useRef<Velocity>({
    angle: Math.random() * 360, // Random initial direction
    speed: MOVEMENT_CONFIG.SPEED,
  });

  // Random rotation configuration for natural variation
  const rotationConfigRef = useRef({
    speeds: {
      x:
        MOVEMENT_CONFIG.ROTATION.SPEED_MIN +
        Math.random() *
          (MOVEMENT_CONFIG.ROTATION.SPEED_MAX -
            MOVEMENT_CONFIG.ROTATION.SPEED_MIN),
      y:
        MOVEMENT_CONFIG.ROTATION.SPEED_MIN +
        Math.random() *
          (MOVEMENT_CONFIG.ROTATION.SPEED_MAX -
            MOVEMENT_CONFIG.ROTATION.SPEED_MIN),
      z:
        MOVEMENT_CONFIG.ROTATION.SPEED_MIN +
        Math.random() *
          (MOVEMENT_CONFIG.ROTATION.SPEED_MAX -
            MOVEMENT_CONFIG.ROTATION.SPEED_MIN),
    },
    phases: {
      x: Math.random() * Math.PI * 2, // Random starting phase
      y: Math.random() * Math.PI * 2,
      z: Math.random() * Math.PI * 2,
    },
  });

  // Animation timing state
  const timeRef = useRef(0);
  const frameCountRef = useRef(0);

  /**
   * Calculate new position with bouncing physics
   */
  const updatePosition = useCallback(() => {
    frameCountRef.current++;

    // Only update position every nth frame for slower movement
    if (frameCountRef.current % MOVEMENT_CONFIG.MOVEMENT_FRAME_SKIP !== 0) {
      return;
    }

    const { angle, speed } = velocityRef.current;

    // Convert angle to radians and calculate movement
    const radians = (angle * Math.PI) / 180;
    const deltaX = Math.cos(radians) * speed;
    const deltaY = Math.sin(radians) * speed;

    setPosition((prevPosition) => {
      let newX = prevPosition.x + deltaX;
      let newY = prevPosition.y + deltaY;
      let newAngle = angle;

      // Wall collision detection and reflection
      const minBound = collisionMargin;
      const maxBound = containerSize - collisionMargin;

      // Hit left or right wall
      if (newX <= minBound || newX >= maxBound) {
        newAngle = 180 - angle; // Basic horizontal reflection
        // Add random variation to prevent repetitive paths
        newAngle += (Math.random() - 0.5) * MOVEMENT_CONFIG.BOUNCE_RANDOMNESS;
        newX = Math.max(minBound, Math.min(maxBound, newX));
      }

      // Hit top or bottom wall
      if (newY <= minBound || newY >= maxBound) {
        newAngle = 360 - angle; // Basic vertical reflection
        // Add random variation to prevent repetitive paths
        newAngle += (Math.random() - 0.5) * MOVEMENT_CONFIG.BOUNCE_RANDOMNESS;
        newY = Math.max(minBound, Math.min(maxBound, newY));
      }

      // Update velocity angle if bounced
      if (newAngle !== angle) {
        velocityRef.current.angle = newAngle;
      }

      return { x: newX, y: newY };
    });
  }, [collisionMargin, containerSize]);

  /**
   * Update 3D rotation with natural oscillation
   */
  const updateRotation = useCallback((deltaTime: number) => {
    timeRef.current += deltaTime * 0.001; // Convert to seconds

    const { speeds, phases } = rotationConfigRef.current;
    const time = timeRef.current;

    setRotation({
      x:
        Math.sin(time * speeds.x + phases.x) *
        MOVEMENT_CONFIG.ROTATION.MAX_DEGREES,
      y:
        Math.sin(time * speeds.y + phases.y) *
        MOVEMENT_CONFIG.ROTATION.MAX_DEGREES,
      z:
        Math.sin(time * speeds.z + phases.z) *
        MOVEMENT_CONFIG.ROTATION.MAX_DEGREES,
    });
  }, []);

  /**
   * Handle return-to-center movement
   */
  const updateReturnToCenter = useCallback(() => {
    const { speed } = velocityRef.current;
    const returnSpeed = speed * MOVEMENT_CONFIG.RETURN_POSITION_SPEED_MULT;

    setPosition((prevPosition) => {
      const deltaX = centerPosition - prevPosition.x;
      const deltaY = centerPosition - prevPosition.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Close enough to center, snap and stop returning
      if (distance < returnSpeed) {
        setIsReturning(false);
        return { x: centerPosition, y: centerPosition };
      }

      // Move toward center at consistent speed
      const moveX = (deltaX / distance) * returnSpeed;
      const moveY = (deltaY / distance) * returnSpeed;

      return {
        x: prevPosition.x + moveX,
        y: prevPosition.y + moveY,
      };
    });
  }, [centerPosition]);

  /**
   * Gradually return rotation to zero
   */
  const updateReturnRotation = useCallback(() => {
    setRotation((prevRotation) => {
      const returnSpeed = MOVEMENT_CONFIG.ROTATION.RETURN_SPEED;

      return {
        x:
          Math.abs(prevRotation.x) > returnSpeed
            ? prevRotation.x - Math.sign(prevRotation.x) * returnSpeed
            : 0,
        y:
          Math.abs(prevRotation.y) > returnSpeed
            ? prevRotation.y - Math.sign(prevRotation.y) * returnSpeed
            : 0,
        z:
          Math.abs(prevRotation.z) > returnSpeed
            ? prevRotation.z - Math.sign(prevRotation.z) * returnSpeed
            : 0,
      };
    });
  }, []);

  // Handle enableFloat changes
  useEffect(() => {
    if (!enableFloat && !isReturning) {
      setIsReturning(true);
    } else if (enableFloat && isReturning) {
      setIsReturning(false);
    }
  }, [enableFloat, isReturning]);

  // Subscribe to RAF for movement updates
  useEffect(() => {
    return subscribe((deltaTime: number) => {
      if (enableFloat && !isReturning) {
        // Normal floating behavior
        updatePosition();
        updateRotation(deltaTime);
      } else if (isReturning) {
        // Return to center behavior
        updateReturnToCenter();
        updateReturnRotation();
      }
    });
  }, [
    subscribe,
    enableFloat,
    isReturning,
    updatePosition,
    updateRotation,
    updateReturnToCenter,
    updateReturnRotation,
  ]);

  return {
    position,
    rotation,
    isReturning,
  };
}
